// @ts-nocheck
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

/**
 * Confirmation dialog centralisé pour remplacer `window.confirm()` (AUD-008).
 *
 * Usage :
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: 'Supprimer ?', message: '...', danger: true });
 *   if (!ok) return;
 *
 * Le `ConfirmProvider` doit être monté une fois en haut de l'app (cf. App.jsx).
 */
const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  // Bouton le moins destructeur (« Annuler »), focalisé à l'ouverture (a11y).
  const cancelBtnRef = useRef(null);
  const dialogRef = useRef(null);
  // Élément focalisé avant l'ouverture, auquel on rend le focus à la fermeture.
  const previousFocusRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      if (typeof document !== 'undefined') previousFocusRef.current = document.activeElement;
      setState({
        title: opts.title || 'Confirmation requise',
        message: opts.message || 'Êtes-vous sûr ?',
        confirmLabel: opts.confirmLabel || 'Confirmer',
        cancelLabel: opts.cancelLabel || 'Annuler',
        danger: Boolean(opts.danger),
        resolve,
      });
    });
  }, []);

  const close = useCallback((result) => {
    if (state) state.resolve(result);
    setState(null);
    // Rendre le focus à l'élément qui avait ouvert le dialogue.
    const precedent = previousFocusRef.current;
    previousFocusRef.current = null;
    if (precedent && typeof precedent.focus === 'function' && document.contains(precedent)) {
      precedent.focus();
    }
  }, [state]);

  // Focus initial sur « Annuler » : une validation par réflexe (Entrée) ne doit
  // pas déclencher l'action destructrice.
  //
  // L'ancien gestionnaire global confirmait sur TOUTE pression d'Entrée, même
  // focus sur « Annuler » : Entrée est désormais laissée au comportement natif
  // du bouton focalisé. Échap annule ; Tab / Maj+Tab bouclent dans le dialogue.
  useEffect(() => {
    if (!state) return undefined;
    cancelBtnRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(false);
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = Array.from(
        dialogRef.current.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusables.length === 0) return;
      const premier = focusables[0];
      const dernier = focusables[focusables.length - 1];
      const actif = document.activeElement;
      const dedans = dialogRef.current.contains(actif);
      if (e.shiftKey && (actif === premier || !dedans)) {
        e.preventDefault();
        dernier.focus();
      } else if (!e.shiftKey && (actif === dernier || !dedans)) {
        e.preventDefault();
        premier.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, close]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
          aria-describedby="confirm-message"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10100,
            padding: '16px',
          }}
          onClick={(e) => {
            // Click sur le backdrop = annulation
            if (e.target === e.currentTarget) close(false);
          }}
        >
          <div
            ref={dialogRef}
            style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              width: '100%',
              maxWidth: '440px',
              padding: '24px',
              animation: 'confirm-pop 0.15s ease-out',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
              {state.danger && (
                <div
                  style={{
                    flexShrink: 0,
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: '#fee2e2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#dc2626',
                  }}
                  aria-hidden
                >
                  <AlertTriangle size={20} />
                </div>
              )}
              <div style={{ flex: 1 }}>
                <h2 id="confirm-title" style={{ margin: 0, fontSize: '1.05rem', color: '#111827', fontWeight: 600 }}>
                  {state.title}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => close(false)}
                aria-label="Fermer"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: '#6b7280',
                }}
              >
                <X size={20} />
              </button>
            </div>
            <p
              id="confirm-message"
              style={{ margin: '0 0 20px', fontSize: '0.9rem', color: '#374151', lineHeight: 1.5 }}
            >
              {state.message}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
              <button
                ref={cancelBtnRef}
                type="button"
                onClick={() => close(false)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {state.cancelLabel}
              </button>
              <button
                type="button"
                onClick={() => close(true)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  background: state.danger ? '#dc2626' : '#7C3AED',
                  color: 'white',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
          <style>{`
            @keyframes confirm-pop {
              from { transform: scale(0.96); opacity: 0; }
              to   { transform: scale(1);    opacity: 1; }
            }
          `}</style>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

/**
 * Hook : retourne une fonction `confirm({ title, message, danger, confirmLabel, cancelLabel })`
 * qui résout en `true` (validé) ou `false` (annulé).
 * Fallback sur `window.confirm` si le provider n'est pas monté (sécurité).
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    return ({ title, message }) =>
      Promise.resolve(window.confirm(`${title || ''}\n\n${message || ''}`.trim()));
  }
  return ctx;
}
